import {Injectable} from '@angular/core';
import {
  DocumentName,
  ExtraRequirementsInquiry,
  HealthAnswersRevision,
  MinifiedUnderwritingProfile,
  ModificationNoticeBreakdown,
  TextTranslations,
  UnderwritingProfile,
} from '@emma-labs/shared/typescript';
import {mergeMap, Observable} from 'rxjs';
import {first, switchMap} from 'rxjs/operators';
import {AuthService} from '../auth.service';
import {UnderwritingProfileApiService} from './underwriting-profile-api.service';

@Injectable({
  providedIn: 'root',
})
export class UnderwritingProfileService {
  constructor(
    private readonly authService: AuthService,
    private readonly api: UnderwritingProfileApiService,
  ) {}

  getAll(): Observable<MinifiedUnderwritingProfile[]> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      switchMap((session) => this.api.getAll(session.id)),
    );
  }

  get(underwritingProfileId: string): Observable<UnderwritingProfile> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      mergeMap((session) => this.api.getById(session.id, underwritingProfileId)),
    );
  }

  saveNote(underwritingProfileId: string, text: string): Observable<UnderwritingProfile> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      mergeMap((session) => this.api.saveNote(session.id, underwritingProfileId, text)),
    );
  }

  updateSurchargeRate(
    underwritingProfileId: string,
    surchargeRate: number | null,
  ): Observable<UnderwritingProfile> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      mergeMap((session) => this.api.updateSurchargeRate(session.id, underwritingProfileId, surchargeRate)),
    );
  }

  updateIsIncludedInContract(
    underwritingProfileId: string,
    isIncludedInContract: boolean,
    healthAnswersRevisionId: string,
  ): Observable<undefined> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      mergeMap((session) =>
        this.api.updateIsIncludedInContract(
          session.id,
          underwritingProfileId,
          isIncludedInContract,
          healthAnswersRevisionId,
        ),
      ),
    );
  }

  createHealthAnswersRevision(
    underwritingProfileId: string,
    healthAnswersRevision: Omit<
      HealthAnswersRevision,
      'id' | 'createdAt' | 'isAutoGenerated' | 'isIncludedInContract'
    >,
  ): Observable<UnderwritingProfile> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      mergeMap((session) =>
        this.api.createHealthAnswersRevision(session.id, underwritingProfileId, healthAnswersRevision),
      ),
    );
  }

  submitExtraRequirements(
    underwritingProfileId: string,
    extraRequirements: Omit<ExtraRequirementsInquiry, 'id' | 'createdAt'>,
  ): Observable<UnderwritingProfile> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      mergeMap((session) =>
        this.api.submitExtraRequirements(session.id, underwritingProfileId, extraRequirements),
      ),
    );
  }

  refuse(underwritingProfileId: string, refusalMessage: TextTranslations): Observable<UnderwritingProfile> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      mergeMap((session) => this.api.refuse(session.id, underwritingProfileId, refusalMessage)),
    );
  }

  approve(
    underwritingProfileId: string,
    modificationNoticeBreakdown?: ModificationNoticeBreakdown,
  ): Observable<UnderwritingProfile> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      mergeMap((session) => this.api.approve(session.id, underwritingProfileId, modificationNoticeBreakdown)),
    );
  }

  cancel(underwritingProfileId: string): Observable<UnderwritingProfile> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      mergeMap((session) => this.api.cancel(session.id, underwritingProfileId)),
    );
  }

  uploadExtraRequirementsDocuments(
    underwritingProfileId: string,
    documents: File[],
  ): Observable<UnderwritingProfile> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      mergeMap((session) =>
        this.api.uploadExtraRequirementsDocuments(session.id, underwritingProfileId, documents),
      ),
    );
  }

  removeExtraRequirementsDocument(
    underwritingProfileId: string,
    documentName: string,
  ): Observable<UnderwritingProfile> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      mergeMap((session) =>
        this.api.removeExtraRequirementsDocument(session.id, underwritingProfileId, documentName),
      ),
    );
  }

  getExtraRequirementsDocument(archivedProposalId: string, documentName: string): Observable<Blob> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      mergeMap((session) =>
        this.api.getExtraRequirementsDocument(session.id, archivedProposalId, documentName),
      ),
    );
  }

  saveUnderwriter(underwritingProfileId: string, agentId?: string): Observable<UnderwritingProfile> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      mergeMap((session) => this.api.saveUnderwriter(session.id, underwritingProfileId, agentId)),
    );
  }

  getDocument(underwritingProfileId: string, document: DocumentName): Observable<Blob> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      switchMap((session) => this.api.getDocument(session.id!, underwritingProfileId, document)),
    );
  }
}
